export const normalizeWhitespace = (s) => String(s ?? '').replace(/\s+/g, ' ').trim();

// ---------------------------------------------------------------------------
// Service description (serviceDesc / serviceDescription)
// ---------------------------------------------------------------------------

// Static marketing copy (fiber serviceDesc) with no dynamic values — exact match after
// whitespace normalization.
const LITERAL_TRANSLATIONS = {
  // 1gbps: $84.95
  'Internet at lightning speeds to handle your critical business needs. Reliable secure connection. WiFi that covers your entire office. No data caps, no modem fees, transparent pricing.':
    'Internet a velocidades ultrarrápidas para satisfacer las necesidades críticas de su empresa. Conexión segura y confiable. Wi-Fi que cubre toda su oficina. Sin límites de datos, sin cargos por módem y con precios transparentes.',
  'Best for remote work, video conferencing, and heavy streaming. No contracts, no early termination fees. Easy self install in minutes':
    'Ideal para trabajo remoto, videoconferencias y transmisión intensiva. Sin contratos ni cargos por cancelación anticipada. Fácil autoinstalación en minutos.',
  'Unlimited data included. Download speeds up to 425 MBPS. Easy, self-install.':
    'Datos ilimitados incluidos. Velocidades de descarga de hasta 425 Mbps. Fácil autoinstalación.',
  'Performance-driven SLA: 99.9% uptime; low latency. 24 x 7 US-based Support and Monitoring. Dedicated connection using line of sight technology. Installation in less than two weeks.':
    'SLA de alto rendimiento: 99.9 % de disponibilidad y baja latencia. Soporte y monitoreo en EE. UU. las 24 horas, los 7 días de la semana. Conexión dedicada mediante tecnología de línea de vista. Instalación en menos de dos semanas.',

  // 2026 product naming/description refresh (Fiber 100/300/500/700 Mbps, 1/2/5 Gig, 5G Unlimited)
  'Perfect starter speed for small households. Stream, browse & video call without lag. No data caps, no throttling. Price-lock guarantee':
    'Velocidad ideal para comenzar en hogares pequeños. Transmita contenido, navegue y haga videollamadas sin interrupciones. Sin límites de datos, sin reducción de velocidad. Garantía de tarifa fija.',
  'For busy, connected households. Run multiple devices without slowdown. No data caps, no throttling. Price-lock guarantee':
    'Para hogares ocupados y conectados. Use múltiples dispositivos sin que se reduzca la velocidad. Sin límites de datos, sin reducción de velocidad. Garantía de tarifa fija.',
  'Most popular speed for super-connected households. Power every device at once, zero lag. No data caps, no throttling. Price-lock guarantee':
    'La velocidad más popular para hogares súper conectados. Use todos sus dispositivos a la vez, sin demoras. Sin límites de datos, sin reducción de velocidad. Garantía de tarifa fija.',
  'For streaming, gaming & remote work. Smart-home ready, zero lag. No data caps, no throttling. Price-lock guarantee':
    'Para transmitir contenido, jugar y trabajar de forma remota. Listo para el hogar inteligente, sin demoras. Sin límites de datos, sin reducción de velocidad. Garantía de tarifa fija.',
  'Top-tiered symmetrical speeds, up and down. Whole home Wi-Fi, zero dead zones. No data caps, no throttling. Price-lock guarantee':
    'Velocidades simétricas de primer nivel, tanto de subida como de bajada. Wi-Fi para toda la casa, sin zonas muertas. Sin límites de datos, sin reducción de velocidad. Garantía de tarifa fija.',
  'Supercharged 2x symmetrical speeds, up and down. Whole home Wi-Fi, zero dead zones. No data caps, no throttling. Price-lock guarantee':
    'Velocidades simétricas potenciadas 2x, tanto de subida como de bajada. Wi-Fi para toda la casa, sin zonas muertas. Sin límites de datos, sin reducción de velocidad. Garantía de tarifa fija.',
  'Our fastest speed, built for power users. Super-fast streaming, competitive gaming, no lag. No data caps, no throttling. Price-lock guarantee':
    'Nuestra velocidad más rápida, diseñada para usuarios exigentes. Transmisión ultrarrápida, juegos competitivos, sin demoras. Sin límites de datos, sin reducción de velocidad. Garantía de tarifa fija.',
  'Up to 425 Mbps. Easy, self-install in minutes. No contracts, no credit checks. Fast shipping':
    'Hasta 425 Mbps. Autoinstalación fácil en minutos. Sin contratos, sin verificación de crédito. Envío rápido.',
  'No contracts, overage fees, or credit checks. Perfect for multiple device connections with 5G speeds. Fast shipping and easy self-install.':
    'Sin contratos, cargos por exceso de uso ni verificación de crédito. Perfecto para conectar múltiples dispositivos con velocidades 5G. Envío rápido y fácil autoinstalación.',
};

// Known English serviceDescription/serviceDesc copy from the product API that has
// no serviceDescriptionEsp/serviceDescEsp counterpart. Speed values are captured so
// the translation still works if the plan's Mbps tier changes.
const TEMPLATES = [
  {
    match: /^No contracts, overage fees, or credit checks\. Perfect for streaming, gaming, and multiple devices with speeds up to (\d+) ?Mbps\. Fast shipping and easy self-install\.$/i,
    translate: ([, speed]) =>
      `Sin contratos, cargos por exceso de uso ni verificación de crédito. Perfecto para transmitir contenido, jugar en línea y usar múltiples dispositivos con velocidades de hasta ${speed} Mbps. Envío rápido y fácil autoinstalación.`,
  },
  {
    match: /^No contracts, overage fees, or credit checks\. Fast shipping and easy self-install\. Perfect for multiple device connections with (\d+) ?GB of data up to (\d+) ?Mbps then slowed thereafter\.?$/i,
    translate: ([, dataCap, speed]) =>
      `Sin contratos, cargos por exceso de uso ni verificación de crédito. Envío rápido y fácil autoinstalación. Perfecto para conectar múltiples dispositivos con ${dataCap} GB de datos a alta velocidad de hasta ${speed} Mbps, con velocidad reducida a partir de entonces.`,
  },
  {
    match: /^Perfect for streaming, gaming, and multiple devices with typical download speeds of (\d+) ?Mbps and max speeds up to (\d+) ?Mbps\. No contracts, overage fees, or credit checks\. Fast shipping and easy self-install\.$/i,
    translate: ([, typical, max]) =>
      `Perfecto para transmitir contenido, jugar en línea y usar múltiples dispositivos con velocidades de descarga típicas de ${typical} Mbps y velocidades máximas de hasta ${max} Mbps. Sin contratos, cargos por exceso de uso ni verificación de crédito. Envío rápido y fácil autoinstalación.`,
  },
  // 500mbps : $725
  {
    match: /^Performance-driven SLA:\s*([\d.]+)% uptime;\s*low latency\.\s*24 x 7 US-based Support and Monitoring\.\s*Dedicated connection using line of sight technology\.\s*Installation in less than two weeks\.$/i,
    translate: ([, uptime]) =>
      `SLA de alto rendimiento: ${uptime}% de disponibilidad y baja latencia. Soporte y monitoreo con sede en Estados Unidos las 24 horas del día, los 7 días de la semana. Conexión dedicada mediante tecnología de línea de vista. Instalación en menos de dos semanas.`,
  },
];

/**
* Returns a hardcoded Spanish translation for known serviceDescription/serviceDesc
* copy, or null if the text doesn't match a known template (caller should fall back
* to the English text in that case).
*/
export const translateKnownServiceDesc = (englishText) => {
  const normalized = normalizeWhitespace(englishText);
  if (!normalized) {return null;}

  if (LITERAL_TRANSLATIONS[normalized]) {return LITERAL_TRANSLATIONS[normalized];}

  for (const { match, translate } of TEMPLATES) {
    const result = normalized.match(match);
    if (result) {return translate(result);}
  }
  return null;
};

/**
* Resolves the description text to render for a plan card: the real base64-encoded
* Esp field when present, else a hardcoded template match, else the English text
* unchanged. Shared across every card renderer so the fallback chain lives in one place.
*/
export const resolveServiceDesc = (spanishLanguagePref, encodedEsp, englishText) => {
  if (!spanishLanguagePref) {return englishText;}

  try {
    const decoded = atob(encodedEsp ?? '');
    if (decoded) {return decoded;}
  } catch (error) {
    // no valid Esp field — fall through to the hardcoded/English fallback below
  }

  return translateKnownServiceDesc(englishText) || englishText;
};

// ---------------------------------------------------------------------------
// Plan name
// ---------------------------------------------------------------------------

const PLAN_NAME_TRANSLATIONS = {
  '5G Home Internet - Unlimited': 'Internet residencial 5G - Ilimitado',
  '5G Home Internet - Max': 'Internet residencial 5G - Máximo',
  'Fiber Internet 100 Mbps': 'Internet de Fibra 100 Mbps',
  'Fiber Internet 500 Mbps': 'Internet de Fibra 500 Mbps',
  'Fiber Internet 1 Gbps': 'Internet de Fibra 1 Gbps',
};

export const translateKnownPlanName = (englishName) => {
  const normalized = normalizeWhitespace(englishName);
  if (!normalized) {return null;}

  return PLAN_NAME_TRANSLATIONS[normalized] || null;
};

export const resolvePlanName = (spanishLanguagePref, englishName) => {
  if (!spanishLanguagePref) {return englishName;}

  return translateKnownPlanName(englishName) || englishName;
};

// ---------------------------------------------------------------------------
// Speed disclaimer
// ---------------------------------------------------------------------------

const DISCLAIMER_TRANSLATIONS = {
  'Actual speeds may vary based on network activity, configuration, and other factors.':
    'products.singlePageDisclaimer',
};

export const resolveSpeedDisclaimer = (disclaimer, t) => {
  if (!disclaimer) {return '';}

  const translationKey = DISCLAIMER_TRANSLATIONS[disclaimer];
  return translationKey ? t(translationKey) : disclaimer;
};

// ---------------------------------------------------------------------------
// Plan-level composed helpers
// ---------------------------------------------------------------------------

export const getPlanFeatures = ({ plan, spanishLanguagePref, fixedWirelessProducts = [] }) => {
  const isWhiProduct =
    Array.isArray(fixedWirelessProducts) &&
    fixedWirelessProducts.some((p) => p.servRef === plan.servRef);

  const englishDesc = isWhiProduct ? plan.serviceDescription : plan.serviceDesc;
  const spanishDesc = isWhiProduct ? plan.serviceDescriptionEsp : plan.serviceDescEsp;

  const description = resolveServiceDesc(spanishLanguagePref, spanishDesc, englishDesc);

  return description
    ?.split(/\.\s+/)
    .map((item) => item.trim())
    .filter(Boolean) || [];
};

// e.g. '500000' -> '500 Mbps'. Shared so the same substitution rule isn't copy-pasted
// wherever a plan's speed needs to render as text.
export const getRawSpeedText = (plan) => plan?.downstreamSpeed?.replace(/000/g, ' Mbps');

export const getPlanName = ({ plan, spanishLanguagePref }) => {
  const rawPlanName = getRawSpeedText(plan) || plan.serviceName;
  return resolvePlanName(spanishLanguagePref, rawPlanName);
};
